import {
  Button,
  ConfirmDialog,
  EmptyState,
  type FilterState,
  InlineMessage,
  type ListDisplayMode,
  type MatrixData,
  matrixFigureId,
  type MatrixSettings,
  ListView,
  PageContent,
  RecordFormDialog,
  SaveViewAsDialog,
  type SortState,
  Tooltip,
  useShortcuts,
  ViewChangeActions,
  ViewPicker,
  ViewShareDialog,
  ViewSharingButton,
} from '@olytic/foundation';
import { Plus, WandSparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DerivedFieldDialog } from '../derived/DerivedFieldDialog';
import { aiColumnKey, isDerivable } from '../derived/derivedApi';
import { useAiColumns } from '../derived/useAiColumns';
import { reviewGuideline, runPlaybookReview } from '../opportunities/strategyApi';
import { useDocumentTitle } from '../documentTitle';
import { restoreNote } from '../setup/recycleBinApi';
import { authenticatedFetch } from '../auth/authClient';
import { objects } from '../config/objects';
import { csvFileName, downloadCsv, EXPORT_PAGE_SIZE, EXPORT_ROW_LIMIT, rowsToCsv } from './exportCsv';
import { loadLookupOptions } from './lookupOptions';
import { mergeSpecOf } from './mergeSpecs';
import { mergeActions, RecordMergeDialog } from './recordMerge';
import {
  cellBody,
  createBody,
  createRecord,
  deleteRecord,
  forEachRecord,
  formFields,
  type ManyOutcome,
  NOT_WRITABLE,
  updateRecord,
  withColumnEditors,
} from './writes';
import {
  browserStorage,
  builderFields,
  DEFAULT_PAGE_SIZE,
  defaultColumns,
  extraColumnKeys,
  kanbanFieldDefs,
  type Labels,
  type ListPrefs,
  loadPrefs,
  readLinkFilters,
  PAGE_SIZES,
  sameViewState,
  savePrefs,
  serverSortKey,
  sortColumnKey,
  resolveDef,
  viewState,
  type ViewState,
  withoutRemovedFields,
  cellFilters,
  matrixMeasures,
  toMatrixData,
} from './listState';
import {
  createListView,
  deleteListView,
  findLinkedView,
  type ListViews,
  loadListViews,
  searchPeople,
  starListView,
  type StoredView,
  toSharedView,
  updateListView,
  type ViewContent,
} from './listViewsApi';
import type { ObjectDef } from './objectDefs';
import { CREATE_PAGES, recordPath } from './routes';
import { listColumns } from './links';
import {
  filterProblem,
  type ObjectListPage as ObjectListResult,
  type ObjectListQuery,
  objectListUrl,
  type ObjectMeta,
  objectMetaUrl,
  type ObjectRow,
  parseObjectListResponse,
  parseObjectMetaResponse,
  toApiFilters,
  todayIso,
  objectMatrixUrl,
  parseObjectMatrixResponse,
} from './objectsApi';


interface ListState {
  /** The last page that loaded; kept on screen while the next one loads. */
  page: ObjectListResult | null;
  error: string | null;
  loading: boolean;
}

/** The most columns a list shows at once (the column chooser's cap). */
const MAX_VISIBLE_COLUMNS = 12;

/** The most rows a Kanban board loads for the current view and filters. */
const KANBAN_ROW_LIMIT = 200;

const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message.length > 0 ? error.message : fallback;

/**
 * One ops table as a library ListView, loaded from GET /api/objects/:object.
 * Its views come from /api/list-views first: who owns them, who sees them,
 * and whether the caller may save, share or delete them is the server's call.
 */
/** An object's field labels, from its record page: the list labels the columns it shows with them. */
function recordLabels(key: ObjectDef['key']): Labels {
  return new Map((objects[key].details ?? []).map((d) => [d.key, typeof d.label === 'string' ? d.label : d.key]));
}

export function ObjectListPage({ def }: { def: ObjectDef }): JSX.Element {
  useDocumentTitle(def.label);
  const [loaded, setLoaded] = useState<{ object: string; views: ListViews } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    loadListViews(authenticatedFetch, def.key, controller.signal).then(
      (views) => {
        if (!controller.signal.aborted) setLoaded({ object: def.key, views });
      },
      (failure: unknown) => {
        if (!controller.signal.aborted) setError(messageOf(failure, 'The views could not be loaded.'));
      },
    );
    return () => controller.abort();
  }, [def.key, attempt]);

  if (loaded === null || loaded.object !== def.key || loaded.views.views.length === 0) {
    return (
      <PageContent as="section" width="wide">
        <ListView<ObjectRow>
          objectLabel={def.label}
          objectIcon={def.icon}
          columns={resolveDef(def, null, recordLabels(def.key)).columns}
          rows={[]}
          getRowId={(row) => row.id}
          loading={error === null}
          error={error ?? (loaded ? 'There are no views for this list.' : undefined)}
          onRefresh={() => setAttempt((n) => n + 1)}
          surface="card"
        />
      </PageContent>
    );
  }
  return <ObjectList key={def.key} def={def} initial={loaded.views} />;
}

/**
 * The list once its views are known. Views, search, the filter builder, sort
 * and paging all run on the server; the field types and picklist values come
 * from /meta. Shown as a table or a Kanban grouped by a picklist. Unsaved view
 * changes live in memory only; page size, the display and the Kanban grouping
 * are remembered in this browser. Record names and lookups link to record pages.
 */
function ObjectList({ def: written, initial }: { def: ObjectDef; initial: ListViews }): JSX.Element {
  const navigate = useNavigate();
  const storage = useMemo(browserStorage, []);
  const [prefs, setPrefs] = useState<ListPrefs>(() => loadPrefs(storage, written.key));
  const updatePrefs = useCallback(
    (patch: Partial<ListPrefs>) =>
      setPrefs((current) => {
        const next = { ...current, ...patch };
        savePrefs(storage, written.key, next);
        return next;
      }),
    [storage, written.key],
  );

  // Field labels from the record page, for the columns the list does not label itself.
  const labels: Labels = useMemo(() => recordLabels(written.key), [written.key]);
  const [meta, setMeta] = useState<ObjectMeta | null>(null);
  // An object described by the schema takes its columns and filters from /meta.
  const def = useMemo(() => resolveDef(written, meta, labels), [written, meta, labels]);
  const objectColumns = useMemo(() => defaultColumns(written), [written]);
  const [listViews, setListViews] = useState<ListViews>(initial);
  const allViews = listViews.views;
  const defaultViewId = listViews.defaultId ?? allViews[0]!.id;

  // A drill-in link (a chart's month, say) opens a view with its own filters in place of the view's.
  const [linkParams] = useSearchParams();
  const [linked] = useState(() => readLinkFilters(linkParams));
  const openingView =
    findLinkedView(allViews, linked?.view) ??
    allViews.find((v) => v.id === defaultViewId) ??
    allViews[0]!;
  const [viewId, setViewId] = useState(openingView.id);
  // What the active view opened with, to tell when the user has changed it.
  const [baseline, setBaseline] = useState<ViewState>(() => viewState(openingView, objectColumns));
  const [current, setCurrent] = useState<ViewState>(() =>
    linked ? { ...baseline, filterRows: linked.rows, filterLogic: linked.logic } : baseline,
  );
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = prefs.pageSize ?? DEFAULT_PAGE_SIZE;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<ListState>({ page: null, error: null, loading: true });
  const writable = meta?.writable ?? NOT_WRITABLE;
  const createPage = CREATE_PAGES[def.key];
  // The definition's columns, then every other column of the table, hidden until picked.
  const linkedColumns = useMemo(
    () => listColumns(def, meta, labels),
    [def, meta, labels],
  );
  // Every column people may change is editable in place: double-click a cell.
  const columns = useMemo(() => withColumnEditors(linkedColumns, writable, loadLookupOptions), [linkedColumns, writable]);
  const [creating, setCreating] = useState(false);
  // ?new=1 (the palette's "New account") opens the New dialog once the object's
  // writability is known, then leaves the address clean.
  const [searchParams, setSearchParams] = useSearchParams();
  const newRequested = searchParams.get('new') === '1';
  useEffect(() => {
    if (newRequested && createPage) {
      navigate(createPage, { replace: true });
      return;
    }
    if (!newRequested || meta === null) return;
    if (meta.writable?.create) setCreating(true);
    setSearchParams((params) => {
      params.delete('new');
      return params;
    }, { replace: true });
  }, [newRequested, meta, setSearchParams, createPage, navigate]);
  const startNew = useCallback(() => (createPage ? navigate(createPage) : setCreating(true)), [createPage, navigate]);
  useShortcuts(
    [{ keys: 'n', handler: startNew, description: `New ${objects[def.key].singular.toLowerCase()}`, group: 'Lists' }],
    { enabled: writable.create || createPage !== undefined },
  );
  const [toDelete, setToDelete] = useState<ObjectRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const metadata = objects[def.key];
  const newFields = useMemo(() => formFields(metadata, writable, loadLookupOptions), [metadata, writable]);

  async function removeRow(row: ObjectRow) {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteRecord(authenticatedFetch, def.key, row.id);
      setToDelete(null);
      setAttempt((n) => n + 1);
    } catch (error) {
      setDeleteError(error instanceof Error && error.message ? error.message : 'Could not delete this record.');
    } finally {
      setDeleting(false);
    }
  }
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Every row seen on any page, so a selection made across pages can be exported.
  const seenRows = useRef(new Map<string, ObjectRow>());
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  // Contacts and accounts: two selected rows merge into one (recordMerge.tsx); a merge deletes one, so it needs delete.
  const mergeSpec = mergeSpecOf(def.key);
  const [merging, setMerging] = useState<[string, string] | null>(null);
  const mergeActionItems = mergeActions(mergeSpec !== null && writable.delete, selectedIds, (ids) => {
    setNotice(null);
    setMerging(ids);
  });

  // Guidelines: the playbook review proposes new ones, inactive until a person accepts or dismisses each.
  const isGuidelines = def.key === 'guidelines';
  const [reviewing, setReviewing] = useState(false);
  async function reviewPlaybook() {
    setReviewing(true);
    setNotice(null);
    try {
      const { run } = await runPlaybookReview();
      const count = Array.isArray(run.output?.proposals) ? run.output.proposals.length : 0;
      if (run.state === 'error') setNotice({ tone: 'error', text: `The playbook review could not run: ${run.error ?? 'unknown error'}` });
      else if (run.state !== 'done') setNotice({ tone: 'success', text: 'The playbook review is queued for the AI service.' });
      else setNotice({ tone: 'success', text: count === 0 ? 'The playbook review proposed nothing new.' : `The playbook review proposed ${count === 1 ? 'one guideline' : `${count} guidelines`}. Accept or dismiss each from its menu.` });
      const proposedView = findLinkedView(allViews, 'proposed');
      if (count > 0 && proposedView && proposedView.id !== activeView.id) openView(proposedView.id);
      setAttempt((n) => n + 1);
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'The playbook review could not run.' });
    } finally {
      setReviewing(false);
    }
  }
  const decide = useCallback(async (row: ObjectRow, decision: 'accept' | 'dismiss') => {
    setNotice(null);
    try {
      await reviewGuideline(row.id, decision);
      setNotice({ tone: 'success', text: `${String(row.name ?? 'The guideline')} ${decision === 'accept' ? 'is in the playbook now' : 'was dismissed'}.` });
      setAttempt((n) => n + 1);
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'That could not be saved.' });
    }
  }, []);
  const notProposed = (row: ObjectRow) => row.review_status !== 'proposed';
  const rowActions = [
    ...(isGuidelines
      ? [
          { label: 'Accept', onSelect: (row: ObjectRow) => void decide(row, 'accept'), hidden: notProposed },
          { label: 'Dismiss', onSelect: (row: ObjectRow) => void decide(row, 'dismiss'), hidden: notProposed },
        ]
      : []),
    ...(writable.delete
      ? [{ label: 'Delete', destructive: true, onSelect: (row: ObjectRow) => { setDeleteError(null); setToDelete(row); } }]
      : []),
  ];

  const rows = state.page?.rows ?? [];
  // AI columns: the object's fields from history, in the column chooser, sorts and the filter builder.
  const ai = useAiColumns(def.key, rows, navigate);
  const [newAiColumn, setNewAiColumn] = useState(false);
  const allColumns = useMemo(() => [...columns, ...ai.columns], [columns, ai.columns]);
  const fields = useMemo(() => [...builderFields(def, meta, labels), ...ai.filterFields], [def, meta, labels, ai.filterFields]);
  const kanbanFields = useMemo(() => kanbanFieldDefs(def, meta, labels), [def, meta, labels]);
  const displayMode: ListDisplayMode = prefs.displayMode ?? 'table';
  const kanban = displayMode === 'kanban';
  const groupBy = [prefs.kanbanGroupBy, def.boardField].find((key) => kanbanFields.some((f) => f.key === key));
  // Matrix: two picklists crossed, aggregated on the server over every record the list holds.
  const matrixOn = displayMode === 'matrix' && kanbanFields.length > 1;
  const measures = useMemo(() => matrixMeasures(meta, labels), [meta, labels]);
  const matrixRow = kanbanFields.find((f) => f.key === prefs.matrix?.rowKey) ?? kanbanFields[0];
  const matrixColumn = kanbanFields.find((f) => f.key === prefs.matrix?.columnKey && f.key !== matrixRow?.key)
    ?? kanbanFields.find((f) => f.key !== matrixRow?.key);
  const matrixSettings: MatrixSettings | null = matrixRow && matrixColumn
    ? {
        rowKey: matrixRow.key,
        columnKey: matrixColumn.key,
        // A figure on a field the table no longer has is dropped.
        figures: (prefs.matrix?.figures ?? []).filter((f) => f.kind === 'count' || measures.some((m) => m.key === f.key)),
      }
    : null;
  if (matrixSettings && matrixSettings.figures.length === 0) matrixSettings.figures = [{ kind: 'count' }];
  const [matrixData, setMatrixData] = useState<MatrixData | null>(null);
  // A view keeps field keys; one the table no longer has is skipped once /meta says what it has.
  const known = useMemo(
    () => (meta === null ? null : { columns: new Set(allColumns.map((c) => c.key)), fields: new Set(fields.map((f) => f.key)) }),
    [meta, allColumns, fields],
  );
  const shown = useMemo(() => withoutRemovedFields(current, known), [current, known]);
  const activeView = allViews.find((v) => v.id === viewId) ?? openingView;
  const recent = activeView.builtIn === 'recent';

  // Field metadata: picklist values for filters (and, later, board lanes).
  useEffect(() => {
    const controller = new AbortController();
    authenticatedFetch(objectMetaUrl(def.key), { signal: controller.signal })
      .then(parseObjectMetaResponse)
      .then(
        (result) => {
          if (!controller.signal.aborted) setMeta(result);
        },
        () => {
          // Without /meta, fixed picklists still work and the others filter as text.
        },
      );
    return () => controller.abort();
  }, [def.key]);

  /** The query for the current view, filters, search and sort, without paging. */
  const query = useMemo((): Omit<ObjectListQuery, 'limit' | 'offset'> => {
    return {
      q: search,
      // A column with a string `sortable` sorts by that field (Name by last_name).
      sort: serverSortKey(allColumns, shown.sort.key),
      dir: shown.sort.direction,
      filters: toApiFilters(shown.filterRows, fields, todayIso()),
      logic: shown.filterLogic,
      columns: extraColumnKeys(shown.columns, meta),
      ...(recent ? { recent: true } : {}),
    };
  }, [shown, search, fields, meta, recent, allColumns]);
  // The header shows a sort on a column's string `sortable` (last_name) on that column (Name):
  // only a displayed header takes it, never a hidden column such as last_name, so a second click
  // on Name turns it. Of two displayed headers sorting by one field, the one clicked keeps it.
  const [sortHeader, setSortHeader] = useState('');
  const headerSort = useMemo(() => {
    const displayed = shown.columns.flatMap((key) => allColumns.find((c) => c.key === key) ?? []);
    return { ...shown.sort, key: sortColumnKey(displayed, shown.sort.key, sortHeader) };
  }, [shown.sort, shown.columns, allColumns, sortHeader]);
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, error: null, loading: true }));
    // A Kanban shows up to KANBAN_ROW_LIMIT rows at once; a table pages.
    const url = objectListUrl(
      def.key,
      kanban ? { ...query, limit: KANBAN_ROW_LIMIT, offset: 0 } : { ...query, limit: pageSize, offset: (page - 1) * pageSize },
    );
    authenticatedFetch(url, { signal: controller.signal })
      .then(parseObjectListResponse)
      .then(
        (result) => {
          if (controller.signal.aborted) return;
          for (const row of result.rows) seenRows.current.set(row.id, row);
          setState({ page: result, error: null, loading: false });
        },
        (error: unknown) => {
          if (controller.signal.aborted) return;
          setState((s) => ({ ...s, error: messageOf(error, `The ${def.noun} could not be loaded.`), loading: false }));
        },
      );
    return () => controller.abort();
    // queryKey stands for query, so an equal query does not refetch.
  }, [def.key, def.noun, queryKey, page, pageSize, attempt, kanban]);

  // The matrix's figures, for the list's search, filters and Recently viewed.
  const matrixKey = matrixOn && matrixSettings && matrixRow && matrixColumn
    ? JSON.stringify([queryKey, matrixSettings.rowKey, matrixSettings.columnKey, matrixSettings.figures.map(matrixFigureId)])
    : null;
  useEffect(() => {
    if (matrixKey === null || !matrixSettings || !matrixRow || !matrixColumn) return;
    const controller = new AbortController();
    setMatrixData(null);
    const url = objectMatrixUrl(def.key, query, {
      rowKey: matrixSettings.rowKey,
      columnKey: matrixSettings.columnKey,
      figureIds: matrixSettings.figures.map(matrixFigureId),
    });
    authenticatedFetch(url, { signal: controller.signal })
      .then(parseObjectMatrixResponse)
      .then(
        (cells) => {
          if (!controller.signal.aborted) setMatrixData(toMatrixData(cells, matrixRow, matrixColumn));
        },
        (error: unknown) => {
          if (controller.signal.aborted) return;
          setNotice({ tone: 'error', text: messageOf(error, 'The matrix could not be loaded.') });
        },
      );
    return () => controller.abort();
    // matrixKey stands for the query and the settings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def.key, matrixKey, attempt]);

  function change(patch: Partial<ViewState>) {
    setCurrent((s) => ({ ...s, ...patch }));
    setPage(1);
  }

  function openView(id: string, views: StoredView[] = allViews) {
    const view = views.find((v) => v.id === id);
    if (!view) return;
    const opened = viewState(view, objectColumns);
    setViewId(id);
    setBaseline(opened);
    setCurrent(opened);
    setPage(1);
  }

  const modified = !sameViewState(shown, withoutRemovedFields(baseline, known));
  const [sharing, setSharing] = useState(false);
  const [savingAs, setSavingAs] = useState(false);

  /** The current columns, filters and sort, as a view stores them. */
  const content = (): ViewContent => ({
    sort: shown.sort.key === '' ? null : shown.sort,
    filters: shown.filterRows,
    logic: shown.filterLogic,
    columns: shown.columns,
  });

  /** Puts a view the server returned in place of the old one, or adds it. */
  function keep(view: StoredView) {
    setListViews((lv) => ({
      ...lv,
      views: lv.views.some((v) => v.id === view.id) ? lv.views.map((v) => (v.id === view.id ? view : v)) : [...lv.views, view],
    }));
  }

  async function saveView() {
    const saved = await updateListView(authenticatedFetch, activeView.id, content());
    keep(saved);
    setBaseline(current);
  }

  async function saveViewAs(label: string) {
    const created = await createListView(authenticatedFetch, def.key, label, content());
    keep(created);
    setViewId(created.id);
    setBaseline(current);
  }

  async function starView(id: string) {
    await starListView(authenticatedFetch, def.key, id);
    setListViews((lv) => ({ ...lv, starredId: id, defaultId: id }));
  }

  /** After a delete: the views as the server now has them, opening the default. */
  async function reloadViews() {
    const next = await loadListViews(authenticatedFetch, def.key);
    setListViews(next);
    openView(next.defaultId ?? next.views[0]?.id ?? '', next.views);
  }

  // Export takes the visible columns, AI ones included (their values come back as `ai:<key>`).
  const visibleColumnDefs = useMemo(
    () => current.columns.flatMap((key) => allColumns.find((c) => c.key === key) ?? []),
    [current.columns, allColumns],
  );
  const exportDerived = visibleColumnDefs.filter((c) => c.key.startsWith('ai:')).map((c) => c.key.slice(3));
  const derivedParam = exportDerived.length > 0 ? `&derived=${exportDerived.map(encodeURIComponent).join(',')}` : '';

  async function exportView() {
    setExporting(true);
    setNotice(null);
    try {
      const rows: ObjectRow[] = [];
      let total = Infinity;
      while (rows.length < Math.min(total, EXPORT_ROW_LIMIT)) {
        const url = `${objectListUrl(def.key, { ...query, limit: EXPORT_PAGE_SIZE, offset: rows.length })}${derivedParam}`;
        const result = await parseObjectListResponse(await authenticatedFetch(url));
        total = result.total;
        rows.push(...result.rows);
        if (result.rows.length === 0) break;
      }
      const kept = rows.slice(0, EXPORT_ROW_LIMIT);
      downloadCsv(csvFileName(def.key, activeView.label, todayIso()), rowsToCsv(visibleColumnDefs, kept));
      setNotice({
        tone: 'success',
        text:
          total > EXPORT_ROW_LIMIT
            ? `Exported the first ${EXPORT_ROW_LIMIT.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} ${def.noun}. Narrow the view to export the rest.`
            : `Exported ${kept.length.toLocaleString('en-US')} ${kept.length === 1 ? 'row' : 'rows'}.`,
      });
    } catch (error) {
      setNotice({ tone: 'error', text: messageOf(error, 'The export failed.') });
    } finally {
      setExporting(false);
    }
  }

  function exportSelected(ids: string[]) {
    const rows = ids.flatMap((id) => seenRows.current.get(id) ?? []);
    downloadCsv(csvFileName(def.key, activeView.label, todayIso(), 'selected'), rowsToCsv(visibleColumnDefs, rows));
    setNotice({ tone: 'success', text: `Exported ${rows.length} selected ${rows.length === 1 ? 'row' : 'rows'}.` });
  }

  // A bulk edit or delete: one request per record. All failing is the dialog's error;
  // some failing is reported here, beside the rows that did change.
  function reportMany(outcome: ManyOutcome, verb: 'Updated' | 'Deleted') {
    const total = outcome.done.length + outcome.failed.length;
    if (outcome.done.length === 0) throw new Error(outcome.failed[0]?.message ?? `Nothing was ${verb.toLowerCase()}.`);
    setAttempt((n) => n + 1);
    setNotice(outcome.failed.length
      ? {
          tone: 'error',
          text: `${verb} ${outcome.done.length} of ${total} ${def.noun}. ${outcome.failed.length} could not be ${verb.toLowerCase()}: ${outcome.failed[0]!.message}`,
        }
      : { tone: 'success', text: `${verb} ${total} ${total === 1 ? 'record' : def.noun}.` });
  }

  async function bulkEdit(ids: string[], key: string, value: string | null) {
    const body = cellBody(columns, writable, key, value);
    reportMany(await forEachRecord(ids, (id) => updateRecord(authenticatedFetch, def.key, id, body)), 'Updated');
  }

  async function bulkDelete(ids: string[]) {
    reportMany(await forEachRecord(ids, (id) => deleteRecord(authenticatedFetch, def.key, id)), 'Deleted');
  }

  const filtered = search.trim() !== '' || current.filterRows.length > 0;

  return (
    <PageContent as="section" width="wide">
      {notice ? (
        <InlineMessage tone={notice.tone} role={notice.tone === 'error' ? 'alert' : 'status'} className="mb-3">
          {notice.text}
        </InlineMessage>
      ) : null}
      <ListView<ObjectRow>
        objectLabel={def.label}
        objectIcon={def.icon}
        title={
          <ViewPicker
            views={allViews.map(toSharedView)}
            activeId={activeView.id}
            defaultId={defaultViewId}
            modified={modified}
            onSelect={(id) => openView(id)}
            onSetDefault={starView}
          />
        }
        columns={allColumns}
        // The column chooser stops at twelve.
        maxVisibleColumns={MAX_VISIBLE_COLUMNS}
        onRowClick={(row) => navigate(recordPath(def.key, row.id))}
        {...(writable.fields.length
          ? {
              onCellSave: async (row: ObjectRow, key: string, value: string | null) => {
                await updateRecord(authenticatedFetch, def.key, row.id, cellBody(columns, writable, key, value));
                // Reload the page of rows so derived columns (names, totals) show the change.
                setAttempt((n) => n + 1);
              },
              onBulkEdit: bulkEdit,
            }
          : {})}
        headerActions={
          <>
            <ViewChangeActions
              view={toSharedView(activeView)}
              modified={modified}
              onSave={saveView}
              onSaveAs={() => setSavingAs(true)}
              onDiscard={() => {
                setCurrent(baseline);
                setPage(1);
              }}
              {...(activeView.whyNotEdit ? { saveDisabledReason: activeView.whyNotEdit } : {})}
            />
            {isGuidelines ? (
              <Button variant="neutral" loading={reviewing} disabled={reviewing} onClick={() => void reviewPlaybook()}>
                <WandSparkles />Review Playbook
              </Button>
            ) : null}
            {writable.create || createPage ? <Button variant="brand" onClick={startNew}><Plus />New</Button> : null}
          </>
        }
        {...(ai.derivable
          ? {
              columnAction: (
                <Tooltip label="New AI column">
                  <Button variant="neutral" size="icon" aria-label="New AI column" onClick={() => setNewAiColumn(true)}><WandSparkles /></Button>
                </Tooltip>
              ),
            }
          : {})}
        viewAction={
          <ViewSharingButton
            view={toSharedView(activeView)}
            currentUserId={listViews.me}
            onClick={() => setSharing(true)}
          />
        }
        // The record page's buttons, from a dropdown at the end of each row.
        {...(rowActions.length ? { rowActionsTrigger: 'chevron' as const, rowActions } : {})}
        {...(writable.delete ? { onBulkDelete: bulkDelete, bulkDeleteNote: restoreNote } : {})}
        {...(mergeActionItems.length ? { massActions: mergeActionItems } : {})}
        visibleColumns={shown.columns}
        onColumnsChange={(columns) => change({ columns })}
        rows={rows}
        total={state.page?.total ?? 0}
        getRowId={(row) => row.id}
        mode="server"
        filterFields={fields}
        filterRows={shown.filterRows}
        filterLogic={shown.filterLogic}
        onFiltersApply={(next: FilterState) => {
          const problem = filterProblem(next.rows, next.logic);
          // Thrown, the builder shows it beside the rows and stays open.
          if (problem) throw new Error(problem);
          change({ filterRows: next.rows, filterLogic: next.logic });
        }}
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchMode="submit"
        sort={headerSort}
        // The view keeps the field the server sorts by, as the built-in views do.
        onSortChange={(sort: SortState) => {
          setSortHeader(sort.key);
          change({ sort: { ...sort, key: serverSortKey(allColumns, sort.key) } });
        }}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        // One Export icon: the selected rows when some are selected, else the view.
        exportActions={[
          {
            key: 'export',
            label: `Export to CSV (up to ${EXPORT_ROW_LIMIT.toLocaleString('en-US')} rows)`,
            onSelect: (ids) => (ids.length > 0 ? exportSelected(ids) : void exportView()),
          },
        ]}
        surface="card"
        density={prefs.density ?? 'comfortable'}
        onDensityChange={(density) => updatePrefs({ density })}
        extendedFilterOperators
        {...(kanban
          ? {}
          : {
              pageSizeOptions: [...PAGE_SIZES],
              onPageSizeChange: (size: number) => {
                updatePrefs({ pageSize: size });
                setPage(1);
              },
            })}
        displayMode={displayMode}
        onDisplayModeChange={(mode) => {
          updatePrefs({ displayMode: mode });
          setPage(1);
        }}
        kanbanFields={kanbanFields}
        // Matrix runs on the server's aggregates; offering it is passing them.
        matrixData={matrixData}
        matrixMeasures={measures}
        {...(matrixSettings ? { matrix: matrixSettings } : {})}
        onMatrixChange={(matrix) => updatePrefs({ matrix })}
        onMatrixCellClick={(row, column) => {
          // The table, narrowed to that cell: a view change, saved like any other.
          change(cellFilters(shown.filterRows, shown.filterLogic, [row, column]));
          updatePrefs({ displayMode: 'table' });
        }}
        {...(groupBy ? { kanbanGroupBy: groupBy } : {})}
        onKanbanGroupByChange={(key) => updatePrefs({ kanbanGroupBy: key })}
        {...(def.kanbanCardFields ? { kanbanCardFields: def.kanbanCardFields } : {})}
        {...(def.kanbanSum ? { kanbanSumField: def.kanbanSum.key, formatKanbanSum: def.kanbanSum.format } : {})}
        pagination={kanban ? 'none' : 'pages'}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onRefresh={() => setAttempt((n) => n + 1)}
        // The library's loading state replaces the table, so it is only for the
        // first load; a refetch keeps the last page on screen.
        loading={state.loading && state.page === null}
        error={state.error ?? undefined}
        empty={
          <EmptyState
            icon={def.icon}
            title={filtered ? `No ${def.noun} match this view` : `No ${def.noun} yet`}
            description={filtered ? 'Try a different search or fewer filters.' : undefined}
          />
        }
      />
      <ViewShareDialog
        view={toSharedView(activeView)}
        open={sharing}
        onOpenChange={setSharing}
        people={listViews.people}
        searchPeople={(query) => searchPeople(authenticatedFetch, query)}
        currentUserId={listViews.me}
        canPublish={listViews.canPublish}
        everyone="Everyone at Olytic"
        everyoneNote="Everyone at Olytic sees it, except Delivery - External people. Only admins can change or delete it."
        onSave={async (change) => keep(await updateListView(authenticatedFetch, activeView.id, change))}
        onDelete={async () => {
          await deleteListView(authenticatedFetch, activeView.id);
          await reloadViews();
        }}
      />
      <SaveViewAsDialog
        open={savingAs}
        onOpenChange={setSavingAs}
        suggested={`${activeView.label} (copy)`}
        onSave={saveViewAs}
      />
      <RecordFormDialog
        open={creating}
        onOpenChange={setCreating}
        title={`New ${metadata.singular.toLowerCase()}`}
        fields={newFields}
        submitLabel="Save"
        onSubmit={async (values) => {
          const id = await createRecord(authenticatedFetch, def.key, createBody(writable, values));
          setCreating(false);
          navigate(recordPath(def.key, id));
        }}
      />
      {isDerivable(def.key) ? (
        <DerivedFieldDialog
          open={newAiColumn}
          onOpenChange={setNewAiColumn}
          object={def.key}
          onSaved={(field) => {
            setNewAiColumn(false);
            // Show the new column straight away; its values fill in as the AI service gets to them.
            change({ columns: [...shown.columns, aiColumnKey(field)] });
            ai.reloadFields();
          }}
        />
      ) : null}
      {mergeSpec ? (
        <RecordMergeDialog
          spec={mergeSpec}
          fetcher={authenticatedFetch}
          ids={merging}
          onClose={() => setMerging(null)}
          onMerged={(id, name) => navigate(recordPath(def.key, id), { state: { notice: `Merged into ${name}.` } })}
          onFailed={(text) => {
            setMerging(null);
            setNotice({ tone: 'error', text });
          }}
        />
      ) : null}
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => { if (!open) setToDelete(null); }}
        title={`Delete this ${metadata.singular.toLowerCase()}?`}
        description={
          <>
            <strong>{toDelete ? (metadata.titleOf?.(toDelete) ?? String(toDelete.name ?? '')) : ''}</strong> moves to the Recycle Bin. {restoreNote()}
            {deleteError ? <InlineMessage tone="error" role="alert" className="mt-3">{deleteError}</InlineMessage> : null}
          </>
        }
        confirmLabel="Delete"
        tone="danger"
        busy={deleting}
        onConfirm={() => { if (toDelete) void removeRow(toDelete); }}
      />
    </PageContent>
  );
}
